import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import TopStrip from '../components/TopStrip';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HelpButton from '../components/HelpButton';
import TestimonialsCarousel from '../components/TestimonialsCarousel';

import heroBanner from '../assets/images/hero-images/manages services.webp';
import heroMobile from '../assets/images/mobile-banners/managed-services-mobile.webp';
import downloadGif from '../assets/images/managed-services/download-animation.gif';
import phpTab from '../assets/images/tab-images/php1.webp';
import meanTab from '../assets/images/tab-images/2.webp';
import javaTab from '../assets/images/tab-images/3.webp';
import servicePdf from '../assets/pdfs/DDevOps-Company-Service-updated_250513_175749.pdf';

const pillars = [
  {
    title:'Proactive Server Management', featured:false,
    items:['24/7 Monitoring & Alerting','OS Patching & Updates','Performance Tuning','Security Hardening','Backup & Recovery Management','Incident Response & Root Cause Analysis'],
  },
  {
    title:'High-Performance Managed Hosting', featured:true,
    items:['Custom Stack Configuration (LAMP, LEMP, MEAN)','Redis & Varnish Caching Layers','CDN Integration & Optimization','SSL Management & HTTP/2','Database Optimization (MySQL, PostgreSQL, MongoDB)','Scalability Planning & Load Balancing'],
  },
  {
    title:'Dedicated IT Operations Team', featured:false,
    items:['Named Technical Account Manager','Priority Ticketing with SLA Guarantee','Proactive Weekly Health Reports','Change Management & Advisory','CI/CD Support & Automation Assistance','On-Call Engineering for Critical Incidents'],
  },
];

const pricingPlans = [
  {
    name:'Standard', subtitle:'For small businesses & startups', price:'$199', period:'/mo', featured:false,
    items:['Up to 2 Managed Servers','24/7 Monitoring & Alerts','OS-Level Security Patching','Daily Backup Management','Email & Chat Support','15-Min Response SLA'],
  },
  {
    name:'Professional', subtitle:'For scaling businesses', price:'$499', period:'/mo', featured:true,
    items:['Up to 5 Managed Servers','Everything in Standard','CI/CD Pipeline Support','Performance Optimization','Dedicated Tech Lead','Phone & Slack Support'],
  },
  {
    name:'Enterprise', subtitle:'For mission-critical systems', price:'Custom', period:'', featured:false,
    items:['Unlimited Managed Servers','Everything in Professional','Infrastructure as Code (IaC)','Geo-Redundancy & DR','Custom SLA Agreement','Direct Engineering Access'],
  },
];

const tabData = [
  { id:'php',  label:'PHP / Laravel', img:phpTab,  content:'Nginx reverse proxy + Varnish HTTP cache + Redis object cache. The result? PHP apps that handle 10x the traffic of a standard LAMP setup.' },
  { id:'mean', label:'MEAN / MERN',   img:meanTab, content:'PM2 process management and Nginx load balancing ensure your Node.js event loop never blocks under massive concurrency.' },
  { id:'java', label:'Java / Spring', img:javaTab, content:'Custom JVM Heap tuning and GC strategies tailored to your application footprint — stability guaranteed for long-running enterprise processes.' },
];

function ManagedServices() {
  const [activeTab, setActiveTab] = useState('php');

  return (
    <>
      <TopStrip />
      <Navbar />

      {/* HERO */}
      <section style={{ marginTop:'125px' }}>
        <div className="container-fluid p-0">
          <img src={heroBanner} width="1600" height="520" alt="Managed Services" className="img-fluid w-100 d-none d-md-block" />
          <img src={heroMobile} width="768" height="1000" alt="Managed Services" className="img-fluid w-100 d-md-none d-block" />
        </div>
      </section>

      {/* EXPERIENCE SECTION */}
      <section className="experience-section">
        <div className="container">
          <div className="row align-items-center gy-5">
            <div className="col-lg-6">
              <div className="experience-card d-flex align-items-center gap-3">
                <div className="experience-number">50+</div>
                <div className="experience-text-lines">
                  <span>YEARS OF</span>
                  <span>IT OPERATIONS</span>
                  <span>COMBINED</span>
                </div>
              </div>
              <div className="experience-evolution text-center text-lg-start mt-3">
                Distilled into <span>Managed DevOps</span>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="experience-content">
                <h3>Full-Service IT Operations, So Your Team Can Focus on Product.</h3>
                <p>We take complete ownership of your servers, security, and infrastructure — monitoring 24/7, patching proactively, and resolving incidents before they impact users. You get enterprise-grade reliability without building an in-house ops team.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-info-subtle py-3 my-5" data-aos="fade-left">
        <div className="container">
          <div className="row text-center">
            {[{n:'99.9%',l:'UPTIME SLA'},{n:'500+',l:'MANAGED ASSETS'},{n:'24/7',l:'LIVE MONITORING'},{n:'15+',l:'YEAR EXPERIENCE'}].map((s,i) => (
              <div className="col-md-3 mb-4 mb-md-0 position-relative" key={i}>
                <h2 className="fw-bold text-info display-5 mb-0">{s.n}</h2>
                <p className="text-uppercase mt-2 fw-semibold">{s.l}</p>
                {i < 3 && <div className="d-none d-md-block position-absolute top-0 end-0 h-100" style={{ width:'1px', backgroundColor:'gray' }}></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE PILLARS */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold display-6">Our Core Managed Pillars</h2>
            <p className="text-muted">Three pillars that power secure, fast, and reliable IT operations</p>
          </div>
          <div className="row g-4">
            {pillars.map((p,i) => (
              <div className="col-md-4" key={i} data-aos="fade-up" data-aos-delay={i*100}>
                <div className={`pillar-card h-100 ${p.featured ? 'featured':''}`}>
                  <h4 className="fw-bold mb-2">{p.title}</h4>
                  <ul className="pillar-list">
                    {p.items.map((item,j) => <li key={j}>{item}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TABS */}
      <section id="tab-section" className="my-5">
        <div className="container text-center">
          <h1 className="top-line mt-5">Curated <span className="text-info">High-Performance</span> Stacks.</h1>
          <p>We benchmark every combination and deploy the exact caching layer your code needs.</p>
        </div>
        <div className="container w-75 my-5 custom-tab-container">
          <ul className="nav nav-tabs justify-content-center" role="tablist">
            {tabData.map(t => (
              <li className="nav-item" key={t.id}>
                <button className={`nav-link ${activeTab===t.id?'active':''}`} onClick={() => setActiveTab(t.id)} type="button">{t.label}</button>
              </li>
            ))}
          </ul>
          <div className="tab-content p-3">
            {tabData.map(t => (
              <div key={t.id} className={`tab-pane fade ${activeTab===t.id?'show active':''}`}>
                <div className="row align-items-center" style={{ minHeight:'300px' }}>
                  <div className="col-lg-6"><p style={{ fontSize:'1rem' }}>{t.content}</p></div>
                  <div className="col-lg-6 text-center"><img src={t.img} loading="lazy" alt={t.id} className="img-fluid" /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="managed-packages py-5" id="services-pkg">
        <div className="container">
          <div className="text-center mb-5">
            <span className="section-tag">Pricing</span>
            <h2 className="fw-bold mb-3">Simple, Transparent Managed IT Pricing</h2>
            <p className="text-muted">No hidden fees. No surprises. Just dependable infrastructure, fully managed.</p>
          </div>
          <div className="row g-4 justify-content-center">
            {pricingPlans.map((plan,i) => (
              <div className="col-lg-4 col-md-6" key={i}>
                <div className={`pricing-card ${plan.featured?'featured':''}`}>
                  <h5 className="fw-bold">{plan.name}</h5>
                  <p className="plan-subtitle">{plan.subtitle}</p>
                  <div className="price">{plan.price}<span>{plan.period}</span></div>
                  <ul>
                    {plan.items.map((item,j) => <li key={j}>✅ {item}</li>)}
                  </ul>
                  <Link to="/contact" className={`btn mt-4 w-100 ${plan.featured?'btn-info text-white':'btn-outline-info'}`}>
                    {plan.featured ? 'Get Started' : 'Contact Sales'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — FLEXIBLE HOSTING */}
      <section className="hosting-cta-section">
        <div className="container">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6">
              <span className="section-label">Flexible Hosting Options</span>
              <h2 className="cta-headline" style={{ textTransform:'capitalize' }}>Still looking for a more affordable managed server?</h2>
              <p className="cta-punchline"><strong className="wait-text">WAIT.</strong> We have got everything from Managed Shared Hosting to Cloud Servers and VPS.</p>
              <p className="cta-description">You don't have to overpay for performance. We have a solution designed for every budget — without compromising speed or reliability.</p>
              <p className="cta-link-text">Get a complete <strong>LIST</strong> of all available packages</p>
              <a href={servicePdf} target="_blank" rel="noreferrer" className="btn btn-info cta-btn">Download Price List (0.5MB)</a>
            </div>
            <div className="col-lg-6 text-center">
              <div className="cta-visual-wrapper">
                <img src={downloadGif} alt="Hosting Price List" width="550" className="img-fluid cta-visual floating-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <TestimonialsCarousel />
      <Footer />
      <HelpButton />
    </>
  );
}

export default ManagedServices;
